/**
 * Runtime validation of a chart specification.
 *
 * The types in `types.ts` make the banned FORMS unconstructible. This file enforces
 * the rules that are about values rather than shapes — element caps, category
 * ordering, the one-line title, distinguisher uniqueness — and it throws rather
 * than warning, because G2 §2.5 lists them as requirements and a warning in a
 * console is not a requirement.
 *
 * It runs in development and in tests. In production it runs too: the cost is a few
 * microseconds per chart and the alternative is shipping a dual-meaning chart to a
 * planner who then makes a buy decision on it.
 */

import { CHART_CAPS } from '../tokens/generated/tokens';
import { TABLE_ROW_CAP } from '../planning/row-cap';
import {
  isMissing, NUMERIC_X_FORMS, type CategoryAxis, type ChartSpec, type NumericAxis, type ReferenceMark, type Series,
} from './types';

/**
 * An event line annotates; it does not carry the finding. Past a handful the markers
 * stop being recessive and become a second, undeclared data layer competing with the
 * series — which is the failure §7.4's hairline treatment exists to avoid.
 */
const MAX_EVENT_LINES = 3;

export class ChartSpecError extends Error {
  constructor(rule: string, detail: string) {
    super(`${detail}  [${rule}]`);
    this.name = 'ChartSpecError';
  }
}

/** G2 §2.5 — the action title is one line, and it is the finding, not the subject. */
const MAX_TITLE_CHARS = 90;

/**
 * G6's layout budget is 40% string expansion. A title that fits at 90 characters in
 * en-GB fits at 126 in the pseudo-locale, which is why the check is applied to the
 * RESOLVED string and the limit is set below the width the layout actually allows.
 */
export function assertOneLineTitle(resolvedTitle: string): void {
  if (resolvedTitle.includes('\n')) {
    throw new ChartSpecError('G2 §2.5', `Action title contains a line break: "${resolvedTitle}". Maximum one line.`);
  }
  if (resolvedTitle.length > MAX_TITLE_CHARS) {
    throw new ChartSpecError(
      'G2 §2.5',
      `Action title is ${resolvedTitle.length} characters and the one-line budget is ${MAX_TITLE_CHARS}: `
      + `"${resolvedTitle}". State the finding, not the subject — "Size 41 is out of stock in 38% of CO doors", `
      + 'not "Stock by size and door with year-on-year comparison". Note that G6 budgets a further 40% '
      + 'expansion for translation, so this limit is already generous.',
    );
  }
}

/**
 * The most rows a heat map draws — the table cap (ADR 9008, `docs/adr/9034`). A heat map's rows
 * are not categorical series: each is a POSITION on a nominal row axis, coloured by the
 * sequential ramp, never by a categorical slot, so the seven-slot palette cap does not bound
 * them. Legibility does: past fifty rows a row stops being a readable row, and the caller ranks
 * its entities and draws the top fifty, saying so in the perimeter.
 */
export const HEATMAP_ROW_CAP = TABLE_ROW_CAP;

function seriesCap(spec: ChartSpec): number {
  switch (spec.form) {
    case 'heatmap': return HEATMAP_ROW_CAP;
    // §7.2: "For scatter, bubble and choropleth — where all pairs are
    // simultaneously visible rather than just adjacent ones — the cap is three
    // series. Past three, facet."
    case 'scatter': return CHART_CAPS.scatterSeries;
    case 'clustered-bar': return CHART_CAPS.clusteredXCategories;
    case 'stacked-bar': return CHART_CAPS.stackedXCategories;
    default: return CHART_CAPS.categoricalSeries;
  }
}

function assertCategoryOrder(axis: CategoryAxis | NumericAxis, series: readonly Series[], where: string): void {
  if (axis.kind !== 'size-scale' && axis.kind !== 'ordinal') return;
  if (axis.order.length === 0) {
    throw new ChartSpecError(
      'design/09 §12.11',
      `${where}: a ${axis.kind} axis must declare its order explicitly. `
      + 'Size axes render in scale order, never lexicographic — EU 40 before EU 41, never 10, 11, 9. '
      + 'The order is declared rather than inferred because inferring it is exactly how 10, 11, 9 happens.',
    );
  }
  const duplicates = axis.order.filter((c, i) => axis.order.indexOf(c) !== i);
  if (duplicates.length) {
    throw new ChartSpecError('design/09 §12.11', `${where}: duplicate categories in the declared order: ${[...new Set(duplicates)].join(', ')}`);
  }
  for (const s of series) {
    if (s.data.length !== axis.order.length) {
      throw new ChartSpecError(
        'design/09 §12.11',
        `${where}: series "${s.id}" has ${s.data.length} points but the ${axis.kind} axis declares `
        + `${axis.order.length} categories (${axis.order.join(', ')}). Every series must be aligned to the `
        + 'declared order, including an explicit missing state where a category has no observation — '
        + 'reqs/00 D8: a size that was not ranged is not a size that sold nothing.',
      );
    }
  }
}

/**
 * Event lines — a marker at a category the axis does not carry is a DEFECT.
 *
 * ECharts places an unmatched category marker at the origin without complaint, so a
 * floor-set handover declared one week off, or against a stale week list, would render
 * as a confident line on the wrong column. That is the single failure mode this
 * annotation can have, so it throws.
 */
function assertEventLines(spec: ChartSpec): void {
  const lines = spec.eventLines;
  if (lines === undefined || lines.length === 0) return;

  const axis = spec.categoryAxis;
  if (axis.kind !== 'size-scale' && axis.kind !== 'ordinal') {
    throw new ChartSpecError(
      'design/09 §7.4',
      `An event line was declared on a ${axis.kind} category axis, which declares no order. `
      + 'A marker can only be placed at a category the axis is known to carry, and a nominal or '
      + 'time axis carries whatever the data happened to contain. Declare the positions — '
      + "kind: 'ordinal' with the weeks in order — and the marker becomes checkable.",
    );
  }

  if (lines.length > MAX_EVENT_LINES) {
    throw new ChartSpecError(
      'design/09 §7.4',
      `${lines.length} event lines were declared and the cap is ${MAX_EVENT_LINES}. An event line `
      + 'annotates recessively; past a handful the markers become a second data layer competing '
      + 'with the series the action title is about.',
    );
  }

  const seen = new Set<string>();
  for (const line of lines) {
    if (!axis.order.includes(line.at)) {
      throw new ChartSpecError(
        'design/09 §7.4',
        `Event line "${line.labelKey}" is positioned at "${line.at}", which the ${axis.kind} axis `
        + `does not carry (${axis.order.join(', ')}). A marker at an unknown category is not drawn `
        + 'where it was meant to be — it is drawn at the origin, and a handover week marked on the '
        + 'wrong column is worse than no marker at all.',
      );
    }
    if (seen.has(line.at)) {
      throw new ChartSpecError(
        'design/09 §7.4',
        `Two event lines share the position "${line.at}". Their labels would overprint, so neither `
        + 'is readable and the table view would list one category as two different events.',
      );
    }
    seen.add(line.at);
    if (line.labelKey.trim() === '') {
      throw new ChartSpecError(
        'G6',
        `The event line at "${line.at}" has an empty labelKey. An unlabelled rule on a chart is a `
        + 'mark the reader has to guess at; the label is resolved from the catalogue, never a literal.',
      );
    }
  }
}

export function assertChartSpec(spec: ChartSpec): void {
  if (spec.series.length === 0 && spec.form !== 'small-multiples') {
    throw new ChartSpecError('G2 §2.5', 'A chart with no series has no finding to title.');
  }

  // Element caps — §2.5.
  const cap = seriesCap(spec);
  if (spec.series.length > cap) {
    throw new ChartSpecError(
      'G2 §2.5 element caps',
      `${spec.form} carries ${spec.series.length} series and the cap is ${cap}. `
      + (spec.form === 'scatter'
        ? 'Past three, facet — in a scatter all pairs are simultaneously visible, not just adjacent ones (§7.2).'
        : spec.form === 'heatmap'
          ? 'A heat map row is a position on a nominal axis and stays readable up to the table cap: rank the '
          + 'entities and draw the top rows, stating how many of how many in the perimeter (ADR 9034).'
          : 'An eighth series folds into "Other", becomes a small multiple, or the chart is faceted (§7.2). '
        + 'The categorical palette is capped at seven slots because the standard eighth is a red, which would '
        + 'sit ΔE ~5 from both brand red and status-critical.'),
    );
  }

  // §2.5 — series distinguishable by more than hue. Not on a heat map: its rows are told apart
  // by their POSITION and their name on the row axis, and the adapter never reads a row's
  // distinguisher (the cells are coloured by the sequential ramp, by value). The row-name check
  // below is the heat map's own form of this rule.
  const distinguishers = spec.series.map((s) => s.distinguisher);
  if (spec.series.length >= 2 && spec.form !== 'heatmap') {
    const dupes = distinguishers.filter((d, i) => distinguishers.indexOf(d) !== i);
    if (dupes.length) {
      throw new ChartSpecError(
        'G2 §2.5',
        `Two series share the distinguisher "${dupes[0]}". Series must be distinguishable by more than hue — `
        + 'pattern, marker or direct label — so the chart survives greyscale printing and colour-vision '
        + 'deficiency. Two series with the same marker are distinguished by hue alone.',
      );
    }
  }

  // §7.4 — colour follows the entity, never its rank.
  const ids = spec.series.map((s) => s.id);
  const dupeIds = ids.filter((id, i) => ids.indexOf(id) !== i);
  if (dupeIds.length) {
    throw new ChartSpecError('G2 §2.5', `Duplicate series id "${dupeIds[0]}". Colour is resolved from the entity id so that filtering a store list does not repaint the survivors; two entities cannot share one id.`);
  }

  // ADR 9050 — what a point carries besides its value: its own x, its label, its fill.
  assertPointExtras(spec);

  // §12.11 — ordering.
  assertCategoryOrder(spec.categoryAxis, spec.series, spec.form);
  if (spec.form === 'heatmap') assertCategoryOrder(spec.rowAxis, [], 'heatmap row axis');
  // A nominal heatmap names each ROW from its series' `nameKey` (the adapter renders
  // `t(s.nameKey)` down the row axis). Two rows on one key are two rows with one name, and a
  // heatmap whose rows cannot be told apart cannot be read at all: audit v4 G-03 found the
  // consolidation screen's five doors all labelled "Door". Distinct ids did not help — the
  // id picks the colour, the key is what the planner reads.
  if (spec.form === 'heatmap' && spec.rowAxis.kind === 'nominal') {
    // The NAME is the key together with its arguments: five doors on `"{door}"` with five
    // different door names are five names.
    const names = spec.series.map((s) => `${s.nameKey}${JSON.stringify(s.nameValues ?? {})}`);
    const shared = spec.series.filter((_, i) => names.indexOf(names[i] ?? '') !== i);
    if (shared.length) {
      throw new ChartSpecError(
        'G2 §2.5',
        `heatmap rows share the name key "${shared[0]?.nameKey}" with the same arguments. Each row is labelled `
        + "from its series' nameKey and nameValues, so rows that resolve to one name cannot be told apart and the "
        + 'heatmap cannot be read. Give each row its own key, or name the entity it is in nameValues.',
      );
    }
  }
  if (spec.form === 'small-multiples') {
    if (spec.panels.length === 0) throw new ChartSpecError('§7.5', 'Small multiples with no panels.');
    spec.panels.forEach((p) => assertCategoryOrder(spec.categoryAxis, p.series, `panel "${p.id}"`));
  }

  // §7.4 — a declared annotation on the category axis, checked against that axis.
  assertEventLines(spec);

  // ADR 9013's chart extension — a numeric x axis and the reference marks drawn on it.
  assertNumericAxis(spec);
  assertReferences(spec);
  // ADR 9051 — an interval names two of the chart's series, on a declared category axis.
  assertInterval(spec);

  // §2.5 — bars encode length from zero.
  const isBarForm = spec.form === 'bar' || spec.form === 'clustered-bar'
    || spec.form === 'stacked-bar' || spec.form === 'paired-bar' || spec.form === 'histogram';
  if (isBarForm && !spec.valueAxis.zeroBased) {
    throw new ChartSpecError(
      'G2 §2.5',
      `${spec.form} has zeroBased: false. A truncated value axis on a bar chart is banned — a bar encodes `
      + 'length from zero, so truncating it makes a 2% difference look like a 40% one. Line charts may zoom; bars may not.',
    );
  }

  // reqs/00 D8 — a missing value is declared, never smuggled in as a number.
  spec.series.forEach((s) => {
    s.data.forEach((d, i) => {
      if (!isMissing(d) && !Number.isFinite(d)) {
        throw new ChartSpecError(
          'reqs/00 D8 / G2 §2.5',
          `Series "${s.id}" point ${i} is ${String(d)}. A gap is a gap, not a joined line and not a zero — `
          + "say which: { missing: 'not-ranged' } for a door that never carried the article, "
          + "{ missing: 'not-reported' } for a feed that did not deliver, or a real 0 for a counted zero.",
        );
      }
    });
  });

  // Issue 226 (ADR 9038, disposition R) — a stated range is one scale for two charts, never a crop.
  assertSharedRange(spec);
}

/**
 * A stated value-axis range (`ValueAxis.sharedRange`): finite, in order, from 0 on a zero-based
 * axis, and holding every datum — a range that left a point off the plot would hide it, and one
 * that started a zero-based axis above 0 would truncate it, which §2.5 bans.
 */
function assertSharedRange(spec: ChartSpec): void {
  const range = spec.valueAxis.sharedRange;
  if (range === undefined) return;
  if (!Number.isFinite(range.min) || !Number.isFinite(range.max) || range.min > range.max) {
    throw new ChartSpecError('ADR 9038', `A shared range must be two finite numbers in order; got ${range.min}..${range.max}.`);
  }
  if (spec.valueAxis.zeroBased && range.min !== 0) {
    throw new ChartSpecError('G2 §2.5', `A zero-based axis starts at 0; a shared range from ${range.min} would truncate it.`);
  }
  spec.series.forEach((s) => {
    s.data.forEach((d, i) => {
      if (!isMissing(d) && (d < range.min || d > range.max)) {
        throw new ChartSpecError(
          'ADR 9038',
          `Series "${s.id}" point ${i} (${d}) lies outside the shared range ${range.min}..${range.max}. `
          + 'The range is one scale for charts compared side by side, never a crop: widen it to hold every point.',
        );
      }
    });
  });
}

/**
 * ADR 9051 — a scatter's interval: two distinct series of the chart hold the bounds, the axis
 * declares its order (a whisker at a category the reader cannot name is unreadable), and no
 * lower bound sits above its upper one — an inverted interval is a defect upstream, not a shape.
 */
function assertInterval(spec: ChartSpec): void {
  if (spec.form !== 'scatter' || spec.interval === undefined) return;
  const { low, high } = spec.interval;
  const lower = spec.series.find((s) => s.id === low);
  const upper = spec.series.find((s) => s.id === high);
  if (lower === undefined || upper === undefined || low === high) {
    throw new ChartSpecError(
      'ADR 9051',
      `The interval names "${low}" and "${high}", which are not two distinct series of this chart. Its bounds are `
      + 'series so that the table view and the file carry them; a bound that is not a series is drawn nowhere else.',
    );
  }
  const axis = spec.categoryAxis;
  if (axis.kind !== 'ordinal' && axis.kind !== 'size-scale') {
    throw new ChartSpecError(
      'ADR 9051',
      `An interval is drawn at each category of a declared axis; this axis is ${axis.kind}. Declare the order `
      + '(ordinal) so every whisker sits at a category the reader can name.',
    );
  }
  lower.data.forEach((d, i) => {
    const u = upper.data[i];
    if (isMissing(d) || u === undefined || isMissing(u)) return;
    if (d > u) {
      throw new ChartSpecError(
        'ADR 9051',
        `At "${axis.order[i] ?? i}" the lower bound (${d}) is above the upper bound (${u}). An inverted interval `
        + 'is a defect in the figures, and drawing it would show a range nobody estimated.',
      );
    }
  });
}

/** A reference mark or numeric x axis is sanctioned on these forms and no others. */
const numericForm = (form: ChartSpec['form']): boolean => (NUMERIC_X_FORMS as readonly string[]).includes(form);

/** At most this many reference marks — the same recession rule as event lines (§7.4). */
const MAX_REFERENCES = 3;

/**
 * A numeric x axis: only on `line`, `area` and `scatter`; one finite x per point; strictly
 * increasing for a line or an area, because a line is drawn in x order and nothing here sorts.
 */
function assertNumericAxis(spec: ChartSpec): void {
  const axis = spec.categoryAxis;
  if (axis.kind !== 'numeric') return;
  if (!numericForm(spec.form)) {
    throw new ChartSpecError(
      'ADR 9013',
      `A numeric x axis was declared on a ${spec.form}. Only line, area and scatter place points at their `
      + 'true x; a bar, a heat map or a diverging bar encodes categories, and spacing them by value would '
      + 'draw a gap that means nothing.',
    );
  }
  if (axis.values.length === 0) {
    throw new ChartSpecError('ADR 9013', `${spec.form}: a numeric x axis with no values has no points to place.`);
  }
  axis.values.forEach((x, i) => {
    if (!Number.isFinite(x)) {
      throw new ChartSpecError('ADR 9013', `${spec.form}: x value ${i} is ${String(x)}. Every point needs a finite x; a point with no y is declared missing, a point with no x is not a point.`);
    }
  });
  if (spec.form !== 'scatter') {
    for (let i = 1; i < axis.values.length; i += 1) {
      if (!(axis.values[i]! > axis.values[i - 1]!)) {
        throw new ChartSpecError(
          'ADR 9013',
          `${spec.form}: x values must strictly increase (value ${i}, ${axis.values[i]}, follows `
          + `${axis.values[i - 1]}). A line joins its points in the order given and the wrapper never sorts, `
          + 'so an out-of-order x would draw a line doubling back on itself.',
        );
      }
    }
  }
  assertLogScale(spec, axis);
  for (const s of spec.series) {
    if (s.at !== undefined) {
      assertOwnPositions(spec, axis, s);
      continue;
    }
    if (s.data.length !== axis.values.length) {
      throw new ChartSpecError(
        'ADR 9013',
        `${spec.form}: series "${s.id}" has ${s.data.length} points but the numeric axis declares `
        + `${axis.values.length} x values. Every series is aligned to the axis, with an explicit missing `
        + 'state where a point has no observation.',
      );
    }
  }
}

/**
 * Reference marks — §12 criterion 14 on a numeric axis: a position the axis does not carry
 * fails the spec. `x` lies inside the x values' range; `y` inside the plotted values' range;
 * `identity` needs the two axes in one unit.
 */
function assertReferences(spec: ChartSpec): void {
  if (!('references' in spec) || spec.references === undefined || spec.references.length === 0) return;
  const axis = spec.categoryAxis;
  if (axis.kind !== 'numeric') {
    throw new ChartSpecError(
      'ADR 9013',
      `${spec.form}: reference marks need a numeric x axis. A category axis carries its markers as event `
      + 'lines at declared categories (§7.4, ADR 0011).',
    );
  }
  if (spec.references.length > MAX_REFERENCES) {
    throw new ChartSpecError(
      'design/09 §7.4',
      `${spec.references.length} reference marks were declared and the cap is ${MAX_REFERENCES}. A reference `
      + 'annotates recessively; past a handful it competes with the series the title is about.',
    );
  }
  const xs = axis.values;
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const ys = spec.series.flatMap((s) => s.data.filter((d): d is number => !isMissing(d)));
  const yMin = Math.min(spec.valueAxis.zeroBased ? 0 : Infinity, ...ys);
  const yMax = Math.max(spec.valueAxis.zeroBased ? 0 : -Infinity, ...ys);
  for (const mark of spec.references) {
    if (mark.labelKey.trim() === '') {
      throw new ChartSpecError('G6', `A ${mark.kind} reference mark has an empty labelKey. An unlabelled rule is a mark the reader has to guess at.`);
    }
    if (mark.kind === 'path') {
      assertPath(spec, mark, { xMin, xMax, yMin, yMax });
    } else if (mark.kind === 'identity') {
      if (axis.unitKey !== spec.valueAxis.unitKey) {
        throw new ChartSpecError(
          'ADR 9013',
          `${spec.form}: a y = x diagonal between an x axis in "${axis.unitKey}" and a value axis in `
          + `"${spec.valueAxis.unitKey}". The diagonal says the two are the same quantity; in two units it `
          + 'asserts an equivalence nobody measured.',
        );
      }
    } else if (!Number.isFinite(mark.at)) {
      throw new ChartSpecError('§12 criterion 14', `The ${mark.kind} reference "${mark.labelKey}" is at ${String(mark.at)}.`);
    } else if (mark.kind === 'x' && (mark.at < xMin || mark.at > xMax)) {
      throw new ChartSpecError(
        '§12 criterion 14',
        `The x reference "${mark.labelKey}" is at ${mark.at}, outside the axis's values (${xMin}–${xMax}). `
        + 'A marker off the axis would be drawn at its edge, which is a position nobody declared.',
      );
    } else if (mark.kind === 'y' && (ys.length === 0 || mark.at < yMin || mark.at > yMax)) {
      throw new ChartSpecError(
        '§12 criterion 14',
        `The y reference "${mark.labelKey}" is at ${mark.at}, outside the plotted values (${yMin}–${yMax}).`,
      );
    }
  }
}

// ── ADR 9050: a log x axis, a series at its own x, a point's label and fill, a trend path ──

/** The forms a log x axis is sanctioned on: points placed by ratio, or a line through them. */
const LOG_FORMS: readonly ChartSpec['form'][] = ['scatter', 'line'];

function assertLogScale(spec: ChartSpec, axis: NumericAxis): void {
  if (axis.scale !== 'log') return;
  if (!LOG_FORMS.includes(spec.form)) {
    throw new ChartSpecError(
      'ADR 9050',
      `A log x axis was declared on a ${spec.form}. Only a scatter or a line places points by ratio; an `
      + 'area fills down to an axis a log scale does not have.',
    );
  }
  if (axis.zeroBased) {
    throw new ChartSpecError('ADR 9050', `${spec.form}: a log axis has no zero — declare zeroBased: false.`);
  }
  const positions = [...axis.values, ...spec.series.flatMap((s) => s.at ?? [])];
  const bad = positions.find((x) => !(x > 0));
  if (bad !== undefined) {
    throw new ChartSpecError(
      'ADR 9050',
      `${spec.form}: x value ${String(bad)} on a log axis. A ratio scale cannot place a point at or below zero; `
      + 'leave such a point off the chart and say so in the perimeter (it stays in the table).',
    );
  }
}

/** A series at its own positions: one x per point, every x on the axis, in order on a line. */
function assertOwnPositions(spec: ChartSpec, axis: NumericAxis, s: Series): void {
  const at = s.at ?? [];
  if (at.length !== s.data.length) {
    throw new ChartSpecError(
      'ADR 9050',
      `${spec.form}: series "${s.id}" has ${s.data.length} points and ${at.length} positions. A series at its `
      + 'own x states one x per point.',
    );
  }
  at.forEach((x, i) => {
    if (!Number.isFinite(x)) {
      throw new ChartSpecError('ADR 9050', `${spec.form}: series "${s.id}" position ${i} is ${String(x)}.`);
    }
    if (!axis.values.includes(x)) {
      throw new ChartSpecError(
        'ADR 9050',
        `${spec.form}: series "${s.id}" is drawn at ${x}, which the axis's values do not list. The axis lists `
        + 'every position any series is drawn at, so its range and the table agree with the marks.',
      );
    }
    if (spec.form !== 'scatter' && i > 0 && !(x > at[i - 1]!)) {
      throw new ChartSpecError(
        'ADR 9050',
        `${spec.form}: series "${s.id}" positions must strictly increase (${x} follows ${at[i - 1]}). A line `
        + 'joins its points in the order given and the wrapper never sorts.',
      );
    }
  });
}

/** `at`, `pointLabels` and `hollow`: numeric axis for `at`, and every array aligned with `data`. */
function assertPointExtras(spec: ChartSpec): void {
  for (const s of spec.series) {
    if (s.at !== undefined && spec.categoryAxis.kind !== 'numeric') {
      throw new ChartSpecError(
        'ADR 9050',
        `${spec.form}: series "${s.id}" states its own x positions on a ${spec.categoryAxis.kind} axis. Own `
        + 'positions are a numeric axis\'s; a category axis aligns every series to its declared order.',
      );
    }
    if (s.pointLabels !== undefined) {
      if (s.pointLabels.key.trim() === '') {
        throw new ChartSpecError('G6', `${spec.form}: series "${s.id}" names its points with an empty key.`);
      }
      if (s.pointLabels.values.length !== s.data.length) {
        throw new ChartSpecError(
          'ADR 9050',
          `${spec.form}: series "${s.id}" labels ${s.pointLabels.values.length} points and carries ${s.data.length}.`,
        );
      }
    }
    if (s.hollow !== undefined && s.hollow.length !== s.data.length) {
      throw new ChartSpecError(
        'ADR 9050',
        `${spec.form}: series "${s.id}" says which of ${s.hollow.length} points are hollow and carries ${s.data.length}.`,
      );
    }
  }
}

/** A trend path: a scatter's, two points or more, x in order and on the axis, y on the plot. */
function assertPath(
  spec: ChartSpec,
  mark: Extract<ReferenceMark, { kind: 'path' }>,
  range: { readonly xMin: number; readonly xMax: number; readonly yMin: number; readonly yMax: number },
): void {
  if (spec.form !== 'scatter') {
    throw new ChartSpecError(
      'ADR 9050',
      `A trend path was declared on a ${spec.form}. It summarises a cloud of points; a line already joins its own.`,
    );
  }
  if (mark.points.length < 2) {
    throw new ChartSpecError('ADR 9050', `The trend "${mark.labelKey}" has ${mark.points.length} point(s); a path needs two.`);
  }
  mark.points.forEach((point, i) => {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      throw new ChartSpecError('ADR 9050', `The trend "${mark.labelKey}" point ${i} is not finite.`);
    }
    if (i > 0 && !(point.x > mark.points[i - 1]!.x)) {
      throw new ChartSpecError('ADR 9050', `The trend "${mark.labelKey}" must run left to right (point ${i}).`);
    }
    if (point.x < range.xMin || point.x > range.xMax || point.y < range.yMin || point.y > range.yMax) {
      throw new ChartSpecError(
        '§12 criterion 14',
        `The trend "${mark.labelKey}" point ${i} (${point.x}, ${point.y}) lies outside the plotted points. A summary `
        + 'of the points cannot sit where no point is.',
      );
    }
  });
}

/** §2.5 — "Maximum three digits on value-axis labels — use k / m and say so in the
 *  unit." Returns the scaled label plus the magnitude suffix for the unit string. */
export function axisScale(maxAbsolute: number): { divisor: number; magnitudeKey: string } {
  const digits = CHART_CAPS.axisLabelDigits;
  const limit = 10 ** digits;
  if (maxAbsolute >= limit * 1000) return { divisor: 1_000_000, magnitudeKey: 'chart.magnitude.millions' };
  if (maxAbsolute >= limit) return { divisor: 1_000, magnitudeKey: 'chart.magnitude.thousands' };
  return { divisor: 1, magnitudeKey: 'chart.magnitude.units' };
}

/**
 * §2.5: "no legend for one series (the title names it); a legend for ≥2 series;
 * direct labels for ≤4 series."
 */
export function legendPolicy(seriesCount: number): { legend: boolean; directLabels: boolean } {
  return {
    legend: seriesCount >= 2,
    directLabels: seriesCount > 0 && seriesCount <= CHART_CAPS.directLabelSeries,
  };
}
